import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

type PlTableDualScrollProps = {
  children: ReactNode;
  /** Tablo içeriği veya kolon seti değişince genişliği yeniden ölç. */
  measureKey: string | number;
};

/**
 * Ant Design Table yatay scroll'unu üstte yansıtır; üst ve alt scrollLeft senkron kalır.
 */
const PlTableDualScroll = ({ children, measureKey }: PlTableDualScrollProps) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const topScrollRef = useRef<HTMLDivElement>(null);
  const bottomScrollRef = useRef<HTMLDivElement>(null);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [needsScroll, setNeedsScroll] = useState(false);
  const syncingRef = useRef(false);

  const measure = useCallback(() => {
    const wrap = wrapRef.current;
    if (!wrap) {
      return;
    }
    const body = wrap.querySelector(".ant-table-body") as HTMLDivElement | null;
    const table = body?.querySelector("table");
    const width = table?.scrollWidth ?? body?.scrollWidth ?? 0;
    const client = body?.clientWidth ?? wrap.clientWidth;
    setScrollWidth(width);
    setNeedsScroll(width > client + 2);
    if (bottomScrollRef.current && body) {
      bottomScrollRef.current.scrollLeft = body.scrollLeft;
    }
  }, []);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) {
      return;
    }
    measure();
    const body = wrap.querySelector(".ant-table-body");
    const table = wrap.querySelector(".ant-table-body table");
    const ro = new ResizeObserver(() => measure());
    if (table) {
      ro.observe(table);
    }
    if (body) {
      ro.observe(body);
    }
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [measure, measureKey]);

  const syncFromTop = () => {
    if (syncingRef.current) {
      return;
    }
    const wrap = wrapRef.current;
    const top = topScrollRef.current;
    const body = wrap?.querySelector(".ant-table-body") as HTMLDivElement | null;
    const bottom = bottomScrollRef.current;
    if (!top || !body) {
      return;
    }
    syncingRef.current = true;
    body.scrollLeft = top.scrollLeft;
    if (bottom) {
      bottom.scrollLeft = top.scrollLeft;
    }
    syncingRef.current = false;
  };

  const syncFromBody = (source: HTMLDivElement) => {
    if (syncingRef.current) {
      return;
    }
    syncingRef.current = true;
    const left = source.scrollLeft;
    if (topScrollRef.current) {
      topScrollRef.current.scrollLeft = left;
    }
    const wrap = wrapRef.current;
    const body = wrap?.querySelector(".ant-table-body") as HTMLDivElement | null;
    const bottom = bottomScrollRef.current;
    if (body && source !== body) {
      body.scrollLeft = left;
    }
    if (bottom && source !== bottom) {
      bottom.scrollLeft = left;
    }
    syncingRef.current = false;
  };

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) {
      return;
    }
    const body = wrap.querySelector(".ant-table-body") as HTMLDivElement | null;
    if (!body) {
      return;
    }
    const onBodyScroll = () => syncFromBody(body);
    body.addEventListener("scroll", onBodyScroll, { passive: true });
    return () => body.removeEventListener("scroll", onBodyScroll);
  }, [measureKey, scrollWidth]);

  const onBottomScroll = () => {
    const bottom = bottomScrollRef.current;
    if (bottom) {
      syncFromBody(bottom);
    }
  };

  return (
    <div
      ref={wrapRef}
      className={`acmd-pl-table-scroll-wrap${needsScroll ? " acmd-pl-table-scroll-wrap--overflow" : ""}`}
    >
      <div
        ref={topScrollRef}
        className="acmd-pl-scroll-top"
        onScroll={syncFromTop}
        aria-hidden={!needsScroll}
      >
        <div className="acmd-pl-scroll-spacer" style={{ width: scrollWidth }} />
      </div>
      <div className="acmd-pl-table-inner">{children}</div>
      <div
        ref={bottomScrollRef}
        className="acmd-pl-scroll-bottom"
        onScroll={onBottomScroll}
        aria-hidden={!needsScroll}
      >
        <div className="acmd-pl-scroll-spacer" style={{ width: scrollWidth }} />
      </div>
    </div>
  );
};

export default PlTableDualScroll;
